import type { OemType } from "@shared/types/oem";
import type { TeamType as Team } from "@shared/types/team";

export type TeamType = Team;

export type UserAiAssistantConfiguration = {
  consumedQueries: number;
  consumedStorage: number;
  autoApproveOCR: boolean;
};

export enum Role {
  OWNER = "OWNER",
  OEM = "OEM",
  STAFF = "STAFF",
  TECHNICIAN = "TECHNICIAN",
  PORTAL_USER = "PORTAL_USER",
}

export type UserStatus = "ACTIVE" | "PENDING" | null;

export type UserType = {
  oem: OemType;
  teams: Team[];
  _id: string;
  info: string;
  skills: { _id: string }[];
  productAccess: string[];
  name: string;
  email: string;
  role: Role;
  hasAiAssistantAccess: boolean;
  emailSignature: string;
  consumedRecordingSeconds: number;
  installedProducts: { type: string; tier: string }[];
  boards: Array<{
    boardFor: string;
    customFields: Array<{
      _id: string;
      label: string;
      options: {
        value: string;
        color: string;
        _id: string;
      };
    }>;
  }>;
  aiAssistantConfiguration: UserAiAssistantConfiguration;
  autoAppendEmailSignature?: boolean;
  dateFormat?: string;
  timeFormat?: string;
  workingHours?: Array<{
    dayOfWeek: number;
    enabled: boolean;
    from: string;
    to: string;
  }>;
  hasPersonalWorkingHours?: boolean;
  status?: UserStatus;
  personalEmailAccounts?: Array<{
    _id: string;
    emailAddress: string;
    emailAccountStatus: string;
    isSynced: boolean;
    needsReconnect?: boolean;
  }>;
  personalMaximumAllowedEmailAddresses?: number;
};

export type ChatCredentials = {
  token: string | null;
  uuid: string | null;
  keys: Record<string, unknown> | null;
  uuidMetadata: Record<string, unknown> | null;
  callChannels: string[] | null;
  notificationChannel: string | null;
  organizationNotificationChannel: string | null;
  notificationChannelGroupName: string | null;
  requestsChannelsGroupNames: string[] | null;
  /** OEM users: Support Ticket channel group (null for non-OEM roles). */
  supportNotificationChannelGroupName: string | null;
  /** Portal users: Support Ticket facility groups, index-aligned with `requestsChannelsGroupNames`. */
  supportRequestsChannelsGroupNames: string[] | null;
  /**
   * Org-level notification channels, one per connection the user has an active
   * Contact on: `channel-{oemId}-{connectionId}-notifications`. Carries the
   * out-of-chat signals (ticket created/status/deleted, chat message) for both
   * modules; the chat messages themselves arrive on the channel *groups* above.
   *
   * Plain channels, NOT channel groups — subscribe by name. Never pass these to
   * `updateSingleMembership`: the grant allows joining, and a membership here
   * would land the channel in the unread map, where the sidebar aggregation
   * would count org events as if they were unread chat messages.
   *
   * Derived from Contact records rather than role, so a dual-role user gets
   * these alongside the OEM channels.
   */
  connectionNotificationChannels: string[] | null;
};
